/**
 * Shared building blocks for every InteractiveSpec schema.
 *
 * Coordinate convention used by every geometric kind (tangram, letter tracing):
 * the play area is a unit square, origin at the TOP LEFT, x to the right,
 * y DOWNWARD — the same axes a canvas or an SVG viewBox uses. Points are
 * normalized to 0..1 so a spec renders identically on a phone and a printout.
 */

import { z } from 'zod'
import type { InteractiveKind, Level, SkillId } from '../types'

/** The rubric owns the 1..5 clamp. Re-exported here so schema code has it to hand. */
export { clampLevel } from '../rubric'

// ─────────────────────────────────────────────────────────────
// Primitives
// ─────────────────────────────────────────────────────────────

export const LevelSchema = z.union([
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(4),
  z.literal(5),
])

/** A SkillId is a branded string; validate the shape, keep the brand. */
export const SkillIdSchema = z.custom<SkillId>(
  (value) => typeof value === 'string' && value.trim().length > 0,
  { message: 'expected a non-empty skill id' },
)

export const NonEmptyString = z.string().trim().min(1)
export const IdSchema = z.string().trim().min(1).max(64)
export const LabelSchema = NonEmptyString.max(80)
export const SentenceSchema = NonEmptyString.max(240)
/** One or two emoji — the whole visual vocabulary for a pre-reader. */
export const EmojiSchema = z.string().min(1).max(8)

/** Design-system token, e.g. "domain.logic". Never a raw hex — theming must hold. */
export const ColorTokenSchema = z
  .string()
  .regex(/^[a-z][a-zA-Z0-9]*(\.[a-zA-Z0-9]+)+$/, 'expected a design token like "domain.logic", not a raw color')

/** Deterministic shuffling: same seed, same experience, replayable. */
export const SeedSchema = z.number().int().min(0).max(2_147_483_647)

export const UnitInterval = z.number().min(0).max(1)

export const NormalizedPointSchema = z.object({ x: UnitInterval, y: UnitInterval })
export type NormalizedPoint = z.infer<typeof NormalizedPointSchema>

export const PolygonSchema = z.array(NormalizedPointSchema).min(3).max(64)

export const AttributesSchema = z.record(NonEmptyString.max(40))

export const SuccessCriteriaSchema = z.object({
  autoLogObservation: z.boolean(),
  thresholds: z.record(z.number()).optional(),
})

// ─────────────────────────────────────────────────────────────
// Spec assembly
// ─────────────────────────────────────────────────────────────

/**
 * `targetSkillIds` may be empty while a spec is being drafted in the Studio.
 * The rule that it must NOT be empty once the activity logs an observation is
 * a cross-field one, enforced in validateSpec — putting it here would turn each
 * spec schema into an effect and break the discriminated union.
 */
export const SPEC_BASE_SHAPE = {
  title: LabelSchema,
  difficulty: LevelSchema,
  theme: NonEmptyString.max(60).optional(),
  targetSkillIds: z.array(SkillIdSchema).max(12),
  successCriteria: SuccessCriteriaSchema,
}

/** Every top-level spec key. Anything else is dropped by repairSpec. */
export const SPEC_TOP_LEVEL_KEYS = [
  'kind',
  'title',
  'difficulty',
  'theme',
  'targetSkillIds',
  'successCriteria',
  'config',
] as const

export function specFor<K extends InteractiveKind, C extends z.ZodTypeAny>(kind: K, config: C) {
  return z.object({ ...SPEC_BASE_SHAPE, kind: z.literal(kind), config }).strict()
}

// ─────────────────────────────────────────────────────────────
// Small pure helpers used by refinements and factories
// ─────────────────────────────────────────────────────────────

export function findDuplicates<T>(values: readonly T[]): T[] {
  const seen = new Set<T>()
  const dupes = new Set<T>()
  for (const value of values) {
    if (seen.has(value)) dupes.add(value)
    seen.add(value)
  }
  return [...dupes]
}

export function isUnique(values: readonly unknown[]): boolean {
  return new Set(values).size === values.length
}

/** Signed area of a closed polyline. Positive = clockwise on screen (y grows downward). */
export function signedArea(points: readonly NormalizedPoint[]): number {
  let sum = 0
  for (let i = 0; i < points.length; i++) {
    const a = points[i]
    const b = points[(i + 1) % points.length]
    sum += a.x * b.y - b.x * a.y
  }
  return sum / 2
}

/** Pick the entry for `level` from a 5-slot ladder. Keeps difficulty tables readable. */
export function byLevel<T>(level: Level, ladder: readonly [T, T, T, T, T]): T {
  return ladder[level - 1]
}

/** Deterministic index cycling — no randomness anywhere in this package. */
export function cycle<T>(values: readonly T[], index: number): T {
  return values[((index % values.length) + values.length) % values.length]
}

export const KIND_LABELS: Record<InteractiveKind, string> = {
  tangram: 'Tangram',
  matrix_reasoning: 'Matrix Puzzle',
  sight_word_deck: 'Sight Word Deck',
  counting_game: 'Counting Game',
  pattern_builder: 'Pattern Builder',
  deduction_puzzle: 'Deduction Puzzle',
  sorting_game: 'Sorting Hunt',
  sequence_order: 'Put It In Order',
  letter_tracing: 'Letter Tracing',
  reading_timer: 'Beat Your Time',
  memory_pairs: 'Memory Pairs',
  model_3d: '3D Model',
  story_builder: 'Story Builder',
}
